/**
 * toChainRows — pure adapter that takes the backend's analysis-results
 * blob and returns ONE row per Threat, with every linked entity
 * resolved + carried inline. The chain-strip column then renders the
 * 7-link chain off this pre-resolved context.
 *
 * Linking logic is ported VERBATIM from the legacy
 * `features/athena/Results/ThreatTraceabilityPage.jsx` (lines 67-107).
 * No data interpretation has changed — Step 1 of the refactor's
 * "data contract frozen" constraint.
 *
 * Returned row shape:
 *   {
 *     id: threat.threat_id,         // React key (== threat_id)
 *     threat_id, threat_scenario, stride_category, compromised_property,
 *     asset:        { asset_id, asset_name, asset_type, ... } | null,
 *     damages:      [{ damage_id, final_impact_rating, ... }],
 *     attackPaths:  [{ attack_path_id, attack_feasibility_rating, ... }],
 *     risks:        [{ risk_id, treatment_decision, risk_value, ... }],
 *     goals:        [{ goal_id, goal_statement, ... }],
 *     claims:       [{ claim_id, claim_statement, ... }],
 *     controls:     [{ control_id, control_name, goal_id, ... }],
 *     requirements: [{ req_id, cal_level, control_id, ... }],
 *   }
 */

const indexBy = (arr, key) => {
  const map = {};
  (arr || []).forEach((item) => {
    if (item[key]) map[item[key]] = item;
  });
  return map;
};

export function toChainRows(results = {}) {
  const threats          = results.threats          ?? [];
  const assets           = results.assets           ?? [];
  const damageScenarios  = results.damageScenarios  ?? results.damage_scenarios  ?? [];
  const attackPaths      = results.attackPaths      ?? results.attack_paths      ?? [];
  const risks            = results.risks            ?? [];
  const goals            = results.goals            ?? results.cybersecurityGoals       ?? [];
  const claims           = results.claims           ?? results.cybersecurityClaims      ?? [];
  const controls         = results.controls         ?? results.cybersecurityControls    ?? [];
  const requirements     = results.requirements     ?? results.cybersecurityRequirements ?? [];

  const assetMap = indexBy(assets, 'asset_id');

  return threats.map((threat) => {
    const asset = assetMap[threat.asset_id] ?? null;

    // Threat → Damage: via threat.damage_ids[] OR ds.asset_ids[] match
    const damages = damageScenarios.filter((ds) =>
      (threat.damage_ids || []).includes(ds.damage_id) ||
      (ds.asset_ids || []).includes(threat.asset_id),
    );

    // Threat → AttackPath: via ap.threat_id
    const linkedAttackPaths = attackPaths.filter((ap) => ap.threat_id === threat.threat_id);

    // Threat → Risk: via r.threat_id
    const linkedRisks = risks.filter((r) => r.threat_id === threat.threat_id);
    const riskIds = new Set(linkedRisks.map((r) => r.risk_id));

    // Risk(Reduce) → Goal: via g.goal_ref_to_risk OR g.risk_ids intersect.
    // Fallback: text match on asset name (matches legacy behaviour).
    const reduceRiskIds = linkedRisks
      .filter((r) => r.treatment_decision === 'Reduce')
      .map((r) => r.risk_id);
    const linkedGoals = goals.filter((g) =>
      (g.goal_ref_to_risk && reduceRiskIds.includes(g.goal_ref_to_risk)) ||
      (g.risk_ids || []).some((id) => riskIds.has(id)) ||
      (g.goal_statement || '').toLowerCase().includes((asset?.asset_name || '').toLowerCase()),
    );
    const goalIds = new Set(linkedGoals.map((g) => g.goal_id));

    // Risk(non-Reduce) → Claim: via c.claim_ref_to_risk OR c.risk_ids intersect.
    const nonReduceRiskIds = linkedRisks
      .filter((r) => r.treatment_decision !== 'Reduce')
      .map((r) => r.risk_id);
    const linkedClaims = claims.filter((c) =>
      (c.claim_ref_to_risk && nonReduceRiskIds.includes(c.claim_ref_to_risk)) ||
      (c.risk_ids || []).some((id) => riskIds.has(id)),
    );

    // Goal → Control: via control.goal_id ∈ goalIds
    const linkedControls = controls.filter((c) => c.goal_id && goalIds.has(c.goal_id));
    const controlIds = new Set(linkedControls.map((c) => c.control_id));

    // Two-hop: Goal → Control → Requirement (or direct Goal → Requirement)
    const linkedReqs = requirements.filter((r) =>
      (r.control_id && controlIds.has(r.control_id)) ||
      (r.goal_id && goalIds.has(r.goal_id)) ||
      (r.linkedGoals || []).some((id) => goalIds.has(id)),
    );

    return {
      id: threat.threat_id,
      threat_id: threat.threat_id,
      threat_scenario: threat.threat_scenario,
      stride_category: threat.stride_category,
      compromised_property: threat.compromised_property,
      asset,
      damages,
      attackPaths: linkedAttackPaths,
      risks: linkedRisks,
      goals: linkedGoals,
      claims: linkedClaims,
      controls: linkedControls,
      requirements: linkedReqs,
    };
  });
}
